import type { ReportType, SocialNetwork, SocialEntryKind } from "../global";
import { ValidationService } from "~/domain/ReportInput/services/validation.service";

/** Resultado da classificação de uma URL de rede social. */
export type SocialUrlClassification =
  | {
    isValid: true;
    network: SocialNetwork;
    entryKind: SocialEntryKind;
    normalizedUrl: string;
  }
  | { isValid: false; error: string };

export type ValidationResult =
  | { isValid: true; type: ReportType }
  | {
    isValid: true;
    type: "redes_sociais";
    network: SocialNetwork | null;
    entryKind: SocialEntryKind;
    normalizedUrl: string;
  }
  | { isValid: false; type: null; error?: string };

/** Classificação de uma entrada social nos modos `url`, `identifier` ou `invalid`. */
export type RedesSociaisEntryClassification =
  | {
    mode: "url";
    network: SocialNetwork;
    entryKind: SocialEntryKind;
    normalizedUrl: string;
  }
  | { mode: "identifier"; identifier: string }
  | { mode: "invalid"; error: string };

/** Numeric_Identifier: apenas dígitos. */
export const NUMERIC_IDENTIFIER_REGEX = /^[0-9]+$/;

/** Comprimento mínimo de um handle "puro" (sem `@`, sem domínio). */
export const SOCIAL_HANDLE_MIN_LENGTH = 3;

/**
 * Handle_Format: 1–30 chars de `[A-Za-z0-9_.-]`, sem `.`/`-` inicial/final,
 * sem `..` nem `--`. Hífen é aceito para cobrir handles/slugs como
 * `felipe-borges-farah-5a555293` (ex.: identificadores de perfil do LinkedIn).
 * `.` e `-` nunca coexistem na mesma entrada: CPF/CNPJ formatados (ex.:
 * `111.444.777-35`) usam ambos os separadores e não podem ser confundidos
 * com um handle.
 */
export const HANDLE_FORMAT_REGEX = /^(?!\.)(?!-)(?!.*\.\.)(?!.*--)(?!.*\..*-)(?!.*-.*\.)[A-Za-z0-9_.-]{1,30}(?<!\.)(?<!-)$/;

/** Verifica se `candidate` casa com o Handle_Format. */
export function isHandleFormat(candidate: string): boolean {
  return HANDLE_FORMAT_REGEX.test(candidate);
}

/** Verifica se `trimmed` é um Explicit_Handle_Marker (`@` + Handle_Format, sem outro `@`). */
export function isExplicitHandleMarker(trimmed: string): boolean {
  if (!trimmed.startsWith("@")) return false;
  const remainder = trimmed.slice(1);
  if (remainder.includes("@")) return false;
  return isHandleFormat(remainder);
}

/**
 * Valida e classifica uma entrada de relatório. Para `redes_sociais` aceita
 * URL/handle/ID numérico; para os demais tipos, classifica cpf/cnpj/email/telefone.
 */
export function isValidReportInput(
  input: string,
  reportType?: string,
): ValidationResult {
  if (typeof input !== 'string') {
    return { isValid: false, type: null };
  }

  const trimmedInput = input.trim();
  if (!trimmedInput) {
    return reportType === 'redes_sociais'
      ? { isValid: false, type: null, error: 'Informe uma URL de perfil ou publicação.' }
      : { isValid: false, type: null };
  }

  if (reportType === 'redes_sociais') {
    const social = classifySocialUrl(trimmedInput);
    if (social.isValid) {
      return {
        isValid: true,
        type: 'redes_sociais',
        network: social.network,
        entryKind: social.entryKind,
        normalizedUrl: social.normalizedUrl,
      };
    }

    const isNumericIdentifier =
      !trimmedInput.includes('@') && NUMERIC_IDENTIFIER_REGEX.test(trimmedInput);

    const isHandle =
      isExplicitHandleMarker(trimmedInput) ||
      (!trimmedInput.includes('@') &&
        isHandleFormat(trimmedInput) &&
        trimmedInput.length >= SOCIAL_HANDLE_MIN_LENGTH);

    if (isNumericIdentifier || isHandle) {
      return {
        isValid: true,
        type: 'redes_sociais',
        network: null,
        entryKind: 'perfil',
        normalizedUrl: trimmedInput,
      };
    }

    return {
      isValid: false,
      type: null,
      error: 'Informe uma URL de perfil ou publicação, ou um @handle.',
    };
  }

  // URLs não são entradas válidas para criar relatórios (demais tipos)
  if (isUrl(trimmedInput)) return { isValid: false, type: null };

  // Placa/chassi têm prioridade sobre a checagem numérica abaixo: um chassi
  // (17 alfanuméricos) tem seus caracteres não-dígitos removidos pelo
  // `replace(/\D/g, "")` a seguir, e o restante de dígitos pode coincidir em
  // comprimento com um CNPJ/telefone válido. Mesma precedência de
  // `ValidationService.validateReportInput`.
  if (ValidationService.validatePlaca(trimmedInput) || ValidationService.validateChassi(trimmedInput)) {
    return { isValid: true, type: 'veiculo' };
  }

  if (trimmedInput.includes('@')) {
    return isEmailValido(trimmedInput)
      ? { isValid: true, type: 'email' }
      : { isValid: false, type: null };
  }

  const cleanInput = trimmedInput.replace(/\D/g, "");
  const len = cleanInput.length;

  if (len < 10 || len > 14) return { isValid: false, type: null };

  if (len === 11 && validarCPF(cleanInput)) {
    return { isValid: true, type: 'cpf' };
  }

  if (len === 14 && validarCNPJ(cleanInput)) {
    return { isValid: true, type: 'cnpj' };
  }

  if (len >= 10 && len <= 13 && isTelefoneValido(cleanInput)) {
    return { isValid: true, type: 'telefone' };
  }

  return { isValid: false, type: null };
}

/**
 * Verifica se a string é uma URL válida
 * @param input - String a ser verificada
 * @returns boolean indicando se é uma URL
 */
export function isUrl(input: string): boolean {
  if (typeof input !== 'string' || !input) return false;

  // Verifica protocolo explícito (http://, https://, ftp://, etc)
  if (/^(https?|ftp|ftps|mailto|file):\/\//i.test(input)) return true;

  // Verifica se começa com www.
  if (/^www\./i.test(input)) return true;

  return false;
}

/** Identifica a rede pelo hostname (conjunto fechado); `null` para outros hosts. */
function detectSocialNetwork(hostname: string): SocialNetwork | null {
  const host = hostname.toLowerCase().replace(/^(www|m|l|mobile)\./, "");

  if (host === "instagram.com" || host.endsWith(".instagram.com") || host === "instagr.am") {
    return "instagram";
  }
  if (
    host === "facebook.com" ||
    host.endsWith(".facebook.com") ||
    host === "fb.com" ||
    host === "fb.watch"
  ) {
    return "facebook";
  }
  if (
    host === "twitter.com" ||
    host.endsWith(".twitter.com") ||
    host === "x.com" ||
    host.endsWith(".x.com")
  ) {
    return "twitter";
  }
  if (host === "linkedin.com" || host.endsWith(".linkedin.com") || host === "lnkd.in") {
    return "linkedin";
  }
  return null;
}

/**
 * Raízes de path do LinkedIn cujo segundo segmento é um slug/id resolvível
 * pelo backend (perfil pessoal, empresa, instituição de ensino, etc.) — espelha
 * o allowlist em `redes_sociais_handle._LI_ENTITY_ROOTS` (backend).
 */
const LINKEDIN_ENTITY_ROOTS = new Set([
  "in",
  "company",
  "school",
  "showcase",
  "newsletter",
  "groups",
  "events",
]);

/** Marcadores de path que identificam uma publicação/artigo do LinkedIn. */
const LINKEDIN_POST_MARKERS = new Set(["posts", "feed", "pulse"]);

/**
 * Verifica se o caminho de uma URL do LinkedIn é um formato suportado
 * (página de entidade com slug/id, ou publicação). Mantém o mesmo
 * fail-closed do backend: qualquer outro caminho é rejeitado aqui em vez de
 * só falhar tarde, no submit.
 */
function isSupportedLinkedInPath(segments: string[]): boolean {
  if (segments.length === 0) return false;
  if (segments.length >= 2 && LINKEDIN_ENTITY_ROOTS.has(segments[0])) return true;
  return segments.some((segment) => LINKEDIN_POST_MARKERS.has(segment));
}

/** Classifica `perfil` vs `post` pelo caminho da URL; default `perfil`. */
function detectEntryKind(network: SocialNetwork, url: URL): SocialEntryKind {
  const segments = url.pathname
    .split("/")
    .map((segment) => segment.toLowerCase())
    .filter(Boolean);

  if (segments.length === 0) return "perfil";

  if (network === "instagram") {
    const instagramPostRoots = new Set(["p", "reel", "reels", "tv", "stories"]);
    return instagramPostRoots.has(segments[0]) ? "post" : "perfil";
  }

  if (network === "twitter") {
    const twitterPostMarkers = new Set(["status", "statuses"]);
    return segments.some((segment) => twitterPostMarkers.has(segment)) ? "post" : "perfil";
  }

  if (network === "linkedin") {
    const linkedinPostMarkers = new Set(["posts", "feed", "pulse"]);
    return segments.some((segment) => linkedinPostMarkers.has(segment)) ? "post" : "perfil";
  }

  // facebook
  const facebookPostMarkers = new Set([
    "posts",
    "photo",
    "photo.php",
    "photos",
    "videos",
    "video.php",
    "watch",
    "permalink.php",
    "story.php",
    "media",
  ]);
  return segments.some((segment) => facebookPostMarkers.has(segment)) ? "post" : "perfil";
}

/**
 * Valida e classifica uma URL de rede social (conjunto fechado de hosts).
 * Assume `https` quando o esquema é omitido e normaliza para `protocolo//host/caminho`.
 */
export function classifySocialUrl(rawUrl: string): SocialUrlClassification {
  if (typeof rawUrl !== "string" || !rawUrl.trim()) {
    return { isValid: false, error: "Informe uma URL de perfil ou publicação." };
  }

  // Só prefixa `https://` sem esquema; esquemas explícitos não-http são rejeitados abaixo.
  const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(rawUrl);
  const candidate = hasScheme ? rawUrl : `https://${rawUrl}`;

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return {
      isValid: false,
      error: "URL inválida. Informe uma URL completa de perfil ou publicação.",
    };
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return { isValid: false, error: "A URL deve usar http ou https." };
  }

  const network = detectSocialNetwork(parsed.hostname);
  if (!network) {
    return {
      isValid: false,
      error: "Apenas URLs de Instagram, Facebook, Twitter ou LinkedIn são aceitas.",
    };
  }

  if (network === "linkedin") {
    const segments = parsed.pathname.split("/").map((segment) => segment.toLowerCase()).filter(Boolean);
    if (!isSupportedLinkedInPath(segments)) {
      return {
        isValid: false,
        error:
          "URL do LinkedIn não suportada. Use um link de perfil (/in/), empresa (/company/), instituição de ensino (/school/) ou outra página de entidade compatível.",
      };
    }
  }

  const entryKind = detectEntryKind(network, parsed);
  const normalizedPath = parsed.pathname.replace(/\/+$/, "");
  const normalizedUrl = `${parsed.protocol}//${parsed.hostname}${normalizedPath}`;

  return { isValid: true, network, entryKind, normalizedUrl };
}

/**
 * `parsed_values.json` uses these exact sentinel strings for EVERY boolean
 * Field_Record ("true" → "Sim", "false" → "Não" for display) — the raw value
 * classification sees is the sentinel, not the translated word. Both happen
 * to satisfy `isHandleFormat` (plain 4–5 char ASCII word), so without this
 * exclusion every boolean field in a redes_sociais profile (VERIFICADO,
 * PRIVADO, CONTA COMERCIAL, ...) is offered as a generate-report candidate.
 */
const BOOLEAN_SENTINEL_VALUES = new Set(["true", "false"]);

/**
 * Real Instagram/Facebook/Twitter numeric IDs run well past this length; a
 * shorter bare digit string turning up while scanning a profile's
 * Field_Records (a follower count, a country code, a city id of "0") is a
 * coincidence of shape, not an identifier. Scoped to the BARE-identifier
 * inference below only — `NUMERIC_IDENTIFIER_REGEX` itself stays permissive
 * for its other use (validating a user's OWN deliberate identifier-field
 * input in the create-report form, where a short value must still work).
 */
const MIN_BARE_NUMERIC_IDENTIFIER_DIGITS = 5;

/**
 * Classifica uma entrada já sabida como social em `url`, `identifier` (handle ou
 * ID numérico) ou `invalid`.
 */
export function classifyRedesSociaisEntry(
  input: string,
): RedesSociaisEntryClassification {
  const invalid: RedesSociaisEntryClassification = {
    mode: "invalid",
    error: "Informe uma URL de perfil ou publicação, ou um identificador válido.",
  };

  if (typeof input !== "string") return invalid;

  const trimmed = input.trim();
  if (!trimmed) return invalid;

  const social = classifySocialUrl(trimmed);
  if (social.isValid) {
    return {
      mode: "url",
      network: social.network,
      entryKind: social.entryKind,
      normalizedUrl: social.normalizedUrl,
    };
  }

  if (isExplicitHandleMarker(trimmed)) {
    return { mode: "identifier", identifier: trimmed.slice(1) };
  }

  if (trimmed.includes("@") || BOOLEAN_SENTINEL_VALUES.has(trimmed.toLowerCase())) {
    return invalid;
  }

  const isPlausibleNumericIdentifier =
    NUMERIC_IDENTIFIER_REGEX.test(trimmed) &&
    trimmed.length >= MIN_BARE_NUMERIC_IDENTIFIER_DIGITS;

  // A genuine handle/username virtually always has at least one letter — a
  // pure digit+underscore/dot/hyphen string that still satisfies
  // `isHandleFormat` (e.g. a hash-like compound field such as "identificador
  // da foto de perfil": "3901421585547403053_1671768728") is a technical
  // value, not something a person would recognize or search by.
  const isPlausibleHandle = isHandleFormat(trimmed) && /[A-Za-z]/.test(trimmed);

  if (isPlausibleNumericIdentifier || isPlausibleHandle) {
    return { mode: "identifier", identifier: trimmed };
  }

  return invalid;
}

export function validarCPF(cpf: string): boolean {
  // Req 5.3: NÃO logar o valor (Valor_Busca) — nem para depurar.

  // Verifica se tem 11 dígitos e não é sequência repetida (111.111.111-11)
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
    return false;
  }

  // Calcula primeiro dígito verificador
  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpf[i]) * (10 - i);
  }
  let resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;

  if (resto !== parseInt(cpf[9])) {
    return false;
  }

  // Calcula segundo dígito verificador
  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpf[i]) * (11 - i);
  }
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;

  return resto === parseInt(cpf[10]);
}

/**
 * Valida formato de email
 * @param email - String com o email completo
 * @returns boolean indicando se o email é válido
 */
export function isEmailValido(email: string): boolean {
  // Req 5.3: NÃO logar o valor (Valor_Busca).
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email) && email.length <= 254;
}

/**
 * Valida telefone brasileiro (fixo ou celular, com ou sem DDD)
 * @param numero - String contendo apenas números (sem máscara)
 * @returns boolean indicando se o telefone é válido
 */
export function isTelefoneValido(numero: string): boolean {
  // Req 5.3: NÃO logar o valor (Valor_Busca).

  //Telefone com +55 no início
  if (numero.startsWith('55')) {
    numero = numero.substring(2);
  }

  // Telefone fixo com DDD: DDD (11-99) + 8 dígitos começando com 2-9
  if (numero.length === 10) {
    const ddd = numero.substring(0, 2);
    const num = numero.substring(2);

    if (/^(\d)\1{7}$/.test(num)) {
      return false;
    }

    return parseInt(ddd) >= 11 && parseInt(ddd) <= 99 && /^[2-9]\d{7}$/.test(num);
  }

  // Telefone celular com DDD: DDD (11-99) + 9 dígitos começando com 9
  if (numero.length === 11) {
    const ddd = numero.substring(0, 2);
    const num = numero.substring(2);

    if (/^(\d)\1{8}$/.test(num)) {
      return false;
    }

    return parseInt(ddd) >= 11 && parseInt(ddd) <= 99 && /^9\d{8}$/.test(num);
  }

  // Telefone fixo sem DDD: 8 dígitos começando com 2-9
  if (numero.length === 8) {
    return /^[2-9]\d{7}$/.test(numero);
  }

  // Telefone celular sem DDD: 9 dígitos começando com 9
  if (numero.length === 9) {
    return /^9\d{8}$/.test(numero);
  }

  return false;
}

/**
 * Valida CNPJ usando algoritmo oficial dos dígitos verificadores
 * @param cnpj - String contendo apenas números (sem máscara)
 * @returns boolean indicando se o CNPJ é válido
 */
export function validarCNPJ(cnpj: string): boolean {
  // Req 5.3: NÃO logar o valor (Valor_Busca).

  // Verifica se tem 14 dígitos e não é sequência repetida
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) {
    return false;
  }

  // Pesos corretos para cálculo dos dígitos verificadores
  const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const pesos2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  // Calcula primeiro dígito verificador
  let soma = 0;
  for (let i = 0; i < 12; i++) {
    soma += parseInt(cnpj[i]) * pesos1[i];
  }
  let resto = soma % 11;
  const dv1 = resto < 2 ? 0 : 11 - resto;

  if (dv1 !== parseInt(cnpj[12])) {
    return false;
  }

  // Calcula segundo dígito verificador
  soma = 0;
  for (let i = 0; i < 13; i++) {
    soma += parseInt(cnpj[i]) * pesos2[i];
  }
  resto = soma % 11;
  const dv2 = resto < 2 ? 0 : 11 - resto;

  return dv2 === parseInt(cnpj[13]);
}